const normalizeGalleryTag = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const parseTaggedGalleryAlt = (altText, knownColors = []) => {
    const fullAlt = normalizeGalleryTag(altText);
    const segments = fullAlt
        .split('|')
        .map(normalizeGalleryTag)
        .filter(Boolean);
    const normalizedColors = [...new Set(knownColors.map(normalizeGalleryTag).filter(Boolean))]
        .sort((left, right) => right.length - left.length);
    const viewPattern = /^(?:amazon\s+)?(?:lifestyle\s+)?(?:front|back|left|right|side|detail|close[\s-]?up|angle|three[\s-]?quarter|other)(?:\s+lifestyle)?$/;
    const imageColor = normalizedColors.find(color => segments.includes(color)) ||
        (normalizedColors.includes(fullAlt) ? fullAlt : '');
    const imageView = segments.find(segment => viewPattern.test(segment)) ||
        segments.find(segment => segment === 'lifestyle') ||
        '';
    const hasLifestyleTag = segments.some(segment => segment.includes('lifestyle'));
    const metadataSegments = segments.filter(segment => (
        segment !== imageColor &&
        segment !== imageView &&
        segment !== 'primary' &&
        segment !== 'image' &&
        segment !== 'garment' &&
        segment !== 'product' &&
        segment !== 'mockup'
    ));
    const isModel = hasLifestyleTag ||
        metadataSegments.some(segment => /^model(?:\s|$)/.test(segment)) ||
        Boolean(imageColor && imageView && metadataSegments.length);

    return {
        fullAlt,
        segments,
        imageColor,
        imageView,
        isPrimary: segments.includes('primary'),
        isModel
    };
};

class VariantSelects extends HTMLElement {
    constructor() {
        super();
        this.item = $(this).closest('.productView');
        this.isFullWidth = this.item.is('.layout-4');
        this.mediaUpdateTimeout = null;
        this.variantChangeTimeout = null;
        this.hasInitializedGallery = false;
        this.lastGalleryColorValue = null;

        this.onVariantInit();
        this.addEventListener('change', this.onVariantChange.bind(this));

        const form = document.getElementById(`product-form-${this.dataset.product}`);
        if (form) {
            form.addEventListener('submit', this.onFormSubmit.bind(this));
        }
    }

    onVariantInit() {
        // This function now handles both slider and grid initialization paths.
        const mainGallery = this.item.find('[data-image-gallery-main]');
        let initAttempts = 0;

        const initialize = () => {
            // The core logic now runs regardless of slider state initially.
            this.performInitialVariantSelection();
            this.updateOptions();
            this.updateMasterId(); // Ensure currentVariant is set
            this.updateImageGallery(); // Ensure gallery reflects the correct variant images
            this.updateAddToCartButtonState();
            document.dispatchEvent(new CustomEvent('variant:init:done', { bubbles: true }));
        };

        const checkGalleryAndInit = () => {
            // Since slick is removed, we just check for the gallery element itself.
            if (mainGallery.length) {
                initialize();
            } else {
                initAttempts++;
                if (initAttempts < 40) { // Try for 2 seconds
                    setTimeout(checkGalleryAndInit, 50);
                } else {
                    initialize(); // Fallback
                }
            }
        };

        checkGalleryAndInit();
    }

    onFormSubmit(event) {
        // Clear all previous errors
        this.querySelectorAll('.variant-option-error').forEach(el => {
            el.style.display = 'none';
            el.textContent = '';
        });
        this.querySelectorAll('.variant-option--highlight').forEach(el => {
            el.classList.remove('variant-option--highlight');
        });
        this.querySelectorAll('.variant-size-error-message').forEach(el => {
            el.style.display = 'none';
        });

        const allOptionsSelected = this.options.every(option => option !== undefined && option !== null && option !== '');

        if (!allOptionsSelected) {
            event.preventDefault();

            const optionWrappers = this.querySelectorAll('[data-option-index]');
            this.options.forEach((optionValue, index) => {
                if (!optionValue) {
                    const wrapper = optionWrappers[index];
                    if (wrapper) {
                        const optionName = wrapper.dataset.optionName?.toLowerCase() || '';
                        
                        if (optionName.includes('size')) {
                            wrapper.classList.add('variant-option--highlight');
                            const sizeError = wrapper.querySelector('.variant-size-error-message');
                            if (sizeError) {
                                sizeError.style.display = 'block';
                            }
                        } else {
                            const errorSpan = wrapper.querySelector('.variant-option-error');
                            if (errorSpan) {
                                let errorMessage = `select a ${optionName}`;
                                if (optionName.includes('size')) {
                                    errorMessage = 'add a size';
                                } else if (optionName.includes('color') || optionName.includes('colour')) {
                                    errorMessage = 'add a colour';
                                }
                                errorSpan.textContent = errorMessage;
                                errorSpan.style.display = 'inline';
                            }
                        }
                    }
                }
            });
        }
    }

    performInitialVariantSelection() {
        const allVariants = this.getVariantData();
        let primaryVariant = null;
        let firstImageContainer;
        const colorOptionIndex = this.dataset.colorOptionIndex !== 'undefined' ? parseInt(this.dataset.colorOptionIndex, 10) : -1;
        const knownColorValues = (
            colorOptionIndex > -1
                ? allVariants
                    .map(v => v.options[colorOptionIndex]?.trim().toLowerCase())
                    .filter(Boolean)
                : []
        );
        const matchesColorLabel = (a, b) => {
            const left = (a || '').trim().toLowerCase();
            const right = (b || '').trim().toLowerCase();

            if (!left || !right) return false;
            return left === right;
        };
        // Since slick is removed, we always use the non-slider method to find the first image.
        firstImageContainer = this.item.find('.productView-image').first();

        // 1. Use the first image's alt text to determine the default color when possible.
        const firstImage = firstImageContainer.find('img');
        if (firstImage.length) {
            const altText = firstImage.attr('alt');
            if (altText) {
                const { imageColor } = parseTaggedGalleryAlt(altText, knownColorValues);
                const primaryColor = imageColor || this.dataset.primaryColor;
                
                if (primaryColor && colorOptionIndex > -1) {
                    const colorToMatch = primaryColor.toLowerCase();
                    if (knownColorValues.some(colorValue => matchesColorLabel(colorValue, colorToMatch))) {
                        primaryVariant = allVariants.find(v => matchesColorLabel(v.options[colorOptionIndex], colorToMatch) && v.available);
                        if (!primaryVariant) {
                            primaryVariant = allVariants.find(v => matchesColorLabel(v.options[colorOptionIndex], colorToMatch));
                        }
                    }
                }
            }
        }

        // 2. Fallback to a variant matching the first slide's media ID.
        if (!primaryVariant) {
            const firstSlideMedia = firstImageContainer.find('[data-media-id]');

            if (firstSlideMedia.length) {
                const mediaIdAttr = firstSlideMedia.data('media-id');
                const mediaId = parseInt(String(mediaIdAttr).split('-').pop(), 10);

                if (!isNaN(mediaId)) {
                    primaryVariant = allVariants.find(v => v.featured_media && v.featured_media.id === mediaId && v.available);
                    if (!primaryVariant) {
                        primaryVariant = allVariants.find(v => v.featured_media && v.featured_media.id === mediaId);
                    }
                }
            }
        }

        // 3. If still no variant, find the first available variant as a last resort.
        if (!primaryVariant) {
            primaryVariant = allVariants.find(v => v.available);
        }

        if (primaryVariant) {
            const colorOptionIndex = this.dataset.colorOptionIndex !== 'undefined' ? parseInt(this.dataset.colorOptionIndex, 10) : -1;

            if (colorOptionIndex === -1) {
                this.updateAttribute(true);
                return;
            }

            const fieldsets = Array.from(this.querySelectorAll('fieldset'));
            const selects = Array.from(this.querySelectorAll('select'));
            let colorOptionSet = false;
            let colorInput;

            const colorValueToSelect = primaryVariant.options[colorOptionIndex];
            
            if (!colorValueToSelect) {
                this.updateAttribute(true);
                return;
            }

            const encodedColorValue = this.encodeOption(colorValueToSelect);

            if (fieldsets.length > 0 && fieldsets[colorOptionIndex]) { // Handle radio buttons
                const colorFieldset = fieldsets[colorOptionIndex];
                const targetInput = colorFieldset.querySelector(`input[value="${encodedColorValue}"]`);
                if (targetInput) {
                    targetInput.checked = true;
                    colorInput = targetInput;
                    colorOptionSet = true;
                }
            } else if (selects.length > 0 && selects[colorOptionIndex]) { // Handle dropdowns
                const colorSelect = selects[colorOptionIndex];
                if ([...colorSelect.options].some(opt => opt.value === encodedColorValue)) {
                    colorSelect.value = encodedColorValue;
                    colorInput = colorSelect;
                    colorOptionSet = true;
                }
            }

            if (colorOptionSet) {
                this.performVariantChange({ target: colorInput });
            } else {
                this.updateAttribute(true);
            }
        } else {
            this.updateAttribute(true);
        }
    }
    
    onVariantChange(event) {
        const optionWrapper = event.target.closest('[data-option-index]');
        if (optionWrapper) {
            const errorSpan = optionWrapper.querySelector('.variant-option-error');
            if (errorSpan) {
                errorSpan.style.display = 'none';
                errorSpan.textContent = '';
            }

            const optionName = optionWrapper.dataset.optionName?.toLowerCase() || '';
            if (optionName.includes('size')) {
                optionWrapper.classList.remove('variant-option--highlight');
                const sizeError = optionWrapper.querySelector('.variant-size-error-message');
                if (sizeError) {
                    sizeError.style.display = 'none';
                }
            }
        }

        event.stopPropagation();
        if (this.variantChangeTimeout) {
            clearTimeout(this.variantChangeTimeout);
        }

        this.variantChangeTimeout = setTimeout(() => {
            this.performVariantChange(event);
        }, 50);
    }

    performVariantChange(event) {
        this.updateOptions();
        this.updateMasterId();
        this.updatePickupAvailability();
        this.updateVariantStatuses();
        this.updateAddToCartButtonState();
      
        this.updateImageGallery();

        const allOptionsSelected = this.options.every(option => option !== undefined && option !== null);

        if (!this.currentVariant) {
            this.updateAttribute(true);
        } else {
            this.updateURL();
            this.updateVariantInput();
            this.renderProductAjaxInfo();
            this.renderProductInfo();
            this.updateProductInfo();
            this.updateAttribute(!allOptionsSelected, !this.currentVariant.available);
            this.checkQuantityWhenVariantChange();
        }
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    }

    decodeOptions() {
        this.options = this.options.map(option => {
            const parsedOption = this.decodeOption(option)
            return parsedOption
        })
    }
  
    decodeOption(option) {
      if (option) {
          return option.split('Special_Double_Quote').join('"').split('Special_Slash').join('/')
        } else {
          return null
        }
    }

    encodeOption(option) {
        if (option) {
          return option.split('"').join('Special_Double_Quote').split('/').join('Special_Slash')
        } else {
          return null
        }
    }
    
    updateMasterId() {
        this.decodeOptions();
        const allVariants = this.getVariantData();

        const isMatching = (variant) => {
            return this.options.every((option, index) => {
                return option === undefined || option === null || variant.options[index] === option;
            });
        };

        this.currentVariant = allVariants.find(v => isMatching(v) && v.available) || allVariants.find(isMatching);
    }      

    updateMedia() {
        if (!this.currentVariant || !this.currentVariant.featured_media) return;

        const mainGalleryEl = this.item.find('[data-image-gallery-main]').get(0);
        const flickityInstance = mainGalleryEl ? Flickity.data(mainGalleryEl) : null;

        const newMediaElement = this.item.find(
            `[data-media-id$="${this.currentVariant.featured_media.id}"]`
        );

        if (!newMediaElement.length) return;

        const imageContainer = newMediaElement.closest('.productView-image');
        if (!imageContainer.length) return;

        if (flickityInstance && window.innerWidth >= 768) {
            const cellElements = flickityInstance.getCellElements();
            const cellIndex = cellElements.findIndex(cell => cell === imageContainer.get(0));

            if (cellIndex !== -1) {
                flickityInstance.select(cellIndex);
            }
        } else {
            // Original logic for non-Flickity views (like mobile before init)
            this.item.find('.productView-image').hide();
            imageContainer.show();
        }
    }

    updateImageGallery() {
        if (!this.currentVariant) {
            return;
        }

        const imageGallery = this.item.find('[data-image-gallery-main]');
        const allImages = imageGallery.find('.productView-image');
        if (!imageGallery.length || !allImages.length) {
            return;
        }

        let variantImageGroup = this.currentVariant.metafields?.custom?.variant_image_group;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const colorOptionIndex = this.dataset.colorOptionIndex !== 'undefined' ? parseInt(this.dataset.colorOptionIndex, 10) : -1;
        const matchesColorLabel = (a, b) => {
            const left = normalizeGalleryTag(a);
            const right = normalizeGalleryTag(b);

            if (!left || !right) return false;
            return left === right;
        };
        const knownColorValues = (
            colorOptionIndex > -1
                ? this.getVariantData()
                    .map(v => normalizeGalleryTag(v.options[colorOptionIndex]))
                    .filter(Boolean)
                : []
        );
        const currentColorValue = colorOptionIndex > -1
            ? normalizeGalleryTag(this.currentVariant.options[colorOptionIndex])
            : '';
        const shouldResetMobileInitialSlide = !this.hasInitializedGallery ||
            (colorOptionIndex > -1 && currentColorValue !== this.lastGalleryColorValue);

        if (typeof variantImageGroup === 'string') {
            variantImageGroup = variantImageGroup.split(',').map(s => s.trim());
        }
        if (!Array.isArray(variantImageGroup)) {
            variantImageGroup = [];
        }

        const allowedAlts = variantImageGroup.map(normalizeGalleryTag).filter(Boolean);
        const imageEntries = allImages.toArray().map((element, index) => {
            if (!element.dataset.galleryOriginalIndex) {
                element.dataset.galleryOriginalIndex = element.dataset.index || String(index + 1);
            }

            const altText = $(element).find('img').first().attr('alt') || '';
            const mediaId = String($(element).find('[data-media-id]').first().data('media-id') || '');

            return {
                element,
                mediaId,
                originalIndex: Number(element.dataset.galleryOriginalIndex) || index + 1,
                tags: parseTaggedGalleryAlt(altText, knownColorValues)
            };
        });
        let matchingEntries = currentColorValue
            ? imageEntries.filter(entry => matchesColorLabel(entry.tags.imageColor, currentColorValue))
            : [...imageEntries];

        if (currentColorValue && this.currentVariant.featured_media?.id) {
            const featuredMediaId = String(this.currentVariant.featured_media.id);
            const featuredEntry = imageEntries.find(entry => entry.mediaId === featuredMediaId);
            const featuredIsAlreadyIncluded = featuredEntry &&
                matchingEntries.some(entry => entry.element === featuredEntry.element);
            const featuredHasNoColorTag = featuredEntry && !featuredEntry.tags.imageColor;

            if (featuredEntry && !featuredIsAlreadyIncluded && featuredHasNoColorTag) {
                matchingEntries.push(featuredEntry);
            }
        }

        // Older products can still rely on the variant image-group metafield. Use it
        // only when the image tags themselves do not identify the selected colour.
        if (!matchingEntries.length && allowedAlts.length) {
            matchingEntries = imageEntries.filter(entry => (
                allowedAlts.includes(entry.tags.fullAlt) ||
                allowedAlts.includes(entry.tags.imageColor)
            ));
        }

        // Last-resort support for old, untagged products: show the selected
        // variant image without leaking images from another colour into the gallery.
        if (!matchingEntries.length && this.currentVariant.featured_media?.id) {
            const featuredMediaId = String(this.currentVariant.featured_media.id);
            matchingEntries = imageEntries.filter(entry => entry.mediaId === featuredMediaId);
        }

        if (!matchingEntries.length && !currentColorValue) {
            matchingEntries = [...imageEntries];
        }

        matchingEntries.sort((left, right) => {
            if (left.tags.isModel !== right.tags.isModel) {
                return left.tags.isModel ? -1 : 1;
            }
            return left.originalIndex - right.originalIndex;
        });

        allImages
            .removeClass('is-first-visible-image is-initial-slide is-variant-visible')
            .hide();

        matchingEntries.forEach(entry => {
            imageGallery.append(entry.element);
            $(entry.element).show().addClass('is-variant-visible');
        });

        const initialImage = matchingEntries.length ? $(matchingEntries[0].element) : $();
        initialImage.addClass('is-first-visible-image');
        if (isMobile && shouldResetMobileInitialSlide) {
            initialImage.addClass('is-initial-slide');
        }

        const thumbnailGallery = this.item.find('.productView-for');
        if (thumbnailGallery.length) {
            const thumbnails = thumbnailGallery.find('.productView-thumbnail');
            const thumbnailsByMediaId = new Map();

            thumbnails.each((index, thumbnail) => {
                const rawMediaId = String(thumbnail.dataset.mediaId || '');
                const mediaId = rawMediaId.split('-').pop();
                thumbnailsByMediaId.set(mediaId, thumbnail);
                $(thumbnail).hide();
            });

            matchingEntries.forEach(entry => {
                const thumbnail = thumbnailsByMediaId.get(entry.mediaId);
                if (thumbnail) {
                    thumbnailGallery.append(thumbnail);
                    $(thumbnail).show();
                }
            });
        }

        const mainGalleryEl = imageGallery.get(0);
        const flickityInstance = mainGalleryEl && typeof Flickity !== 'undefined'
            ? Flickity.data(mainGalleryEl)
            : null;
        if (flickityInstance) {
            flickityInstance.reloadCells();
            flickityInstance.resize();
        }

        this.hasInitializedGallery = true;
        this.lastGalleryColorValue = currentColorValue || null;
        document.dispatchEvent(new CustomEvent('variant:images:updated', { detail: { variant: this.currentVariant } }));
    }

    scrollToBlock(block) {
        const headerHeight = document.querySelectorAll('.section-header-navigation')[0]?.getBoundingClientRect().height 
        const announcementBarHeight = document.querySelectorAll('.announcement-bar')[0]?.getBoundingClientRect().height 
        const positionTop = block.getBoundingClientRect().top - headerHeight - announcementBarHeight 

        window.scrollTo({
            top: positionTop,
            behavior: 'smooth'
        })
    }

    updateURL() {
        if (!this.currentVariant) return;
        window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}`);

        productForms.forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (this.currentVariant?.available) {
            pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
            pickUpAvailability.removeAttribute('available');
            pickUpAvailability.innerHTML = '';
        }
    }

    renderProductAjaxInfo() {
        if (!this.currentVariant) return;
        const variantId = this.currentVariant.id;
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                if (this.currentVariant.id !== variantId) return;

                const id = `product-price-${this.dataset.product}`;
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destination = document.getElementById(id);
                const source = html.getElementById(id);

                const property = `product-property-${this.dataset.product}`;
                const destinationProperty = document.getElementById(property);
                const sourceProperty = html.getElementById(property);

                if (source && destination) {
                    destination.innerHTML = source.innerHTML;
                }

                if (this.checkNeedToConvertCurrency()) {
                    let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');

                    Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                }

                if(destinationProperty) {
                    if (sourceProperty) {
                        destinationProperty.innerHTML = sourceProperty.innerHTML;
                        destinationProperty.style.display = 'table';
                    } else{
                        destinationProperty.style.display = 'none';
                    }
                } else if (sourceProperty) {
                    document.querySelector('.productView-product').insertBefore(sourceProperty, document.querySelector('.productView-options'));
                }

                document.getElementById(`product-price-${this.dataset.product}`)?.classList.remove('visibility-hidden');
        });
    }

    renderProductInfo() {
        if(this.item.find('[data-sku]').length > 0){
            this.item.find('[data-sku] .productView-info-value').text(this.currentVariant.sku);
        }

        var inventory = this.currentVariant?.inventory_management;

        if(inventory != null) {
            var arrayInVarName = `product_inven_array_${this.dataset.product}`,
                inven_array = window[arrayInVarName];

            if(inven_array != undefined) {
                var inven_num = inven_array[this.currentVariant.id],
                    inventoryQuantity = parseInt(inven_num);

                this.item.find('input[name="quantity"]').attr('data-inventory-quantity', inventoryQuantity);

                if(this.item.find('[data-inventory]').length > 0){
                    if(inventoryQuantity > 0){
                        const showStock = this.item.find('[data-inventory]').data('stock-level');
                        if (showStock == 'show') {
                            this.item.find('[data-inventory] .productView-info-value').text(inventoryQuantity+' '+window.inventory_text.inStock);
                        }
                        else {
                            this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.inStock);
                        }
                    } else {
                        this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.outOfStock);
                    }
                }

                hotStock(inventoryQuantity);
            }
        }

        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const optionSticky = sticky.find('.select__select');

            optionSticky.val(this.currentVariant.id);
        }
    }

    updateProductInfo() {
        if (!this.currentVariant) return;
        const variantId = this.currentVariant.id;
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                if (this.currentVariant.id !== variantId) return;

                const description = `[data-product-description-${this.dataset.product}]`
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destinationDesc = document.querySelector(description);
                const sourceDesc = html.querySelector(description);

                if (sourceDesc && destinationDesc) {
                    destinationDesc.innerHTML = sourceDesc.innerHTML;
                    if (destinationDesc.closest('.toggle-content--height')){
                        destinationDesc.style.maxHeight = null;
                    }
                }
        });
    }

    updateAttribute(unavailable = true, disable = true){
        const addButton = document.getElementById(`product-form-${this.dataset.product}`)?.querySelector('[name="add"]');
        var quantityInput = this.item.find('input[name="quantity"]'),
            notifyMe = this.item.find('.productView-notifyMe'),
            hotStock = this.item.find('.productView-hotStock'),
            buttonAddtocart = this.item.find('.product-form__submit'),
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));

        if (isNaN(maxValue)) {
            maxValue = maxValue = parseInt(buttonAddtocart.attr('data-inventory-quantity'));
        } else {
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
        }
        
        if(unavailable){
            var text = window.variantStrings.addToCart;

            quantityInput.attr('disabled', false);
            notifyMe.slideUp('slow');
            addButton.removeAttribute('disabled');
            addButton.textContent = text;
            quantityInput.closest('quantity-input').removeClass('disabled');

            if(hotStock.length > 0) hotStock.addClass('is-hiden');
        } else {
            if (disable) {
                var text = window.variantStrings.soldOut;

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', true);
                addButton.setAttribute('disabled', true);
                addButton.textContent = text;
                quantityInput.closest('quantity-input').addClass('disabled');

                if(notifyMe.length > 0){
                    notifyMe.find('input[name="halo-notify-product-variant"]').val(this.currentVariant.title);
                    notifyMe.find('.notifyMe-text').empty();
                    notifyMe.slideDown('slow');
                }
            } else{
                var text,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

                if(window.subtotal.show) {
                    let qty = quantityInput.val();

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal') 
                    moneySpan.innerText = subTotal 
                    document.body.appendChild(moneySpan) 

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }
                    
                    subTotal = moneySpan.innerText 
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                       if (pdView_subTotal != null) {
                         pdView_subTotal.textContent = subTotal;
                       }
                      
                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.variantStrings.addToCart;
                        }
                    }
                    else if (window.subtotal.style == '2') {
                        text = window.subtotal.text.replace('[value]', subTotal);
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', false);
                addButton.removeAttribute('disabled');
                addButton.textContent = text;
                quantityInput.closest('quantity-input').removeClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val();
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if(notifyMe.length > 0){
                    notifyMe.slideUp('slow');
                }
            }
        }
    }

    updateStickyAddToCart(unavailable = true, disable = true){
        if(this.item.find('.productView-stickyCart').length > 0){
            const input = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('input[name="id"]');
            const button = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('[name="add"]');
            const buttonSpan = button?.querySelector('span');

            if (!button || !input || !buttonSpan) return;

            var quantityInput = this.item.find('input[name="quantity"]');
            var maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
          
            if(unavailable){
                var text = window.variantStrings.unavailable;

                button.setAttribute('disabled', true);
                buttonSpan.textContent = text;
            } else {
                if (!this.currentVariant) return;

                if (disable) {
                    var text = window.variantStrings.soldOut;

                    button.setAttribute('disabled', true);
                    buttonSpan.textContent = text;
                } else {
                    let text;
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }

                    button.removeAttribute('disabled');
                    buttonSpan.textContent = text;
                }

                input.value = this.currentVariant.id;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    updateAddToCartButtonState() {
        const productForm = document.getElementById(`product-form-${this.dataset.product}`);
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const selectOptionsButton = productForm.querySelector('[name="select-options"]');

        if (!addButton || !selectOptionsButton) return;

        if (this.getVariantData().length <= 1) {
            addButton.style.display = '';
            selectOptionsButton.style.display = 'none';
            return;
        }

        const allOptionsSelected = this.options.every(option => option !== undefined && option !== null && option !== '');

        if (allOptionsSelected) {
            addButton.style.display = '';
            selectOptionsButton.style.display = 'none';
        } else {
            addButton.style.display = 'none';
            selectOptionsButton.style.display = '';
            
            selectOptionsButton.onclick = () => {
                const optionWrappers = this.querySelectorAll('[data-option-index]');

                this.options.forEach((optionValue, index) => {
                    if (!optionValue) {
                        const wrapper = optionWrappers[index];
                        if (wrapper) {
                            const optionName = wrapper.dataset.optionName?.toLowerCase() || '';
                            
                            if (optionName.includes('size')) {
                                wrapper.classList.add('variant-option--highlight');
                                const sizeError = wrapper.querySelector('.variant-size-error-message');
                                if (sizeError) {
                                    sizeError.style.display = 'block';
                                }
                            } else {
                                const errorSpan = wrapper.querySelector('.variant-option-error');
                                if (errorSpan) {
                                    let errorMessage = `select a ${optionName}`;
                                    if (optionName.includes('size')) {
                                        errorMessage = 'add a size';
                                    } else if (optionName.includes('color') || optionName.includes('colour')) {
                                        errorMessage = 'add a colour';
                                    }
                                    errorSpan.textContent = errorMessage;
                                    errorSpan.style.display = 'inline';
                                }
                            }
                        }
                    }
                });
            };
        }
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }

    checkNeedToConvertCurrency() {
        var currencyItem = $('.dropdown-item[data-currency]');
        if (currencyItem.length) {
            return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
        } else {
            return;
        }
    }

    checkQuantityWhenVariantChange() {
        var quantityInput = this.closest('.productView-details').querySelector('input.quantity__input')
        var maxValue = parseInt(quantityInput?.dataset.inventoryQuantity);
        var inputValue = parseInt(quantityInput?.value);

        let value = inputValue 

        if (inputValue > maxValue && maxValue > 0) {
            value = maxValue
        } else {
            value = inputValue
        }

        if (value < 1 || isNaN(value)) value = 1 
      
        if (quantityInput) {
          quantityInput.value = value
        }
      
        document.getElementById('product-add-to-cart').dataset.available = this.currentVariant.available && maxValue <= 0
    }

    updateVariantStatuses() {
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        const optionsCount = inputWrappers.length;

        // Get current selections
        const selectedOptions = new Array(optionsCount);
        inputWrappers.forEach((wrapper, index) => {
            const checkedInput = wrapper.querySelector('input:checked');
            selectedOptions[index] = checkedInput ? checkedInput.value : null;
        });

        inputWrappers.forEach((option, index) => {
            // Update header text
            const checkedInput = option.querySelector(':checked');
            if (checkedInput) {
                option.querySelector('[data-header-option]').innerText = checkedInput.value;

                const swatchNameDisplay = option.nextElementSibling?.querySelector('[data-swatch-name-display]');
                if (swatchNameDisplay) {
                    swatchNameDisplay.innerText = checkedInput.value;
                }
            }

            const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];

            // Find all possible and available values for the current option, given other selections
            let filteredVariants = this.getVariantData();
            for (let i = 0; i < optionsCount; i++) {
                if (i !== index && selectedOptions[i]) {
                    filteredVariants = filteredVariants.filter(variant => variant.options[i] === selectedOptions[i]);
                }
            }

            const optionInputsValue = [...new Set(filteredVariants.map(variant => variant.options[index]))];
            const availableOptionInputsValue = [...new Set(filteredVariants.filter(variant => variant.available).map(variant => variant.options[index]))];

            this.setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue);
        });
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach(input => {
            if (availableOptionInputsValue.includes(input.getAttribute('value'))) {
                input.classList.remove('soldout');
                input.innerText = input.getAttribute('value');
            } else {
                input.classList.add('soldout');
                optionInputsValue.includes(input.getAttribute('value')) ? input.innerText = input.getAttribute('value') + ' (Sold out)' : input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'))
            }
        });
    }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
    constructor() {
        super();
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach(input => {
            if (availableOptionInputsValue.includes(input.getAttribute('value'))) {
                input.nextSibling.classList.remove('soldout', 'unavailable');
                input.nextSibling.classList.add('available');
            } else {
                input.nextSibling.classList.remove('available', 'unavailable');
                input.nextSibling.classList.add('soldout');

                if (window.variantStrings.hide_variants_unavailable && !optionInputsValue.includes(input.getAttribute('value'))) {
                    input.nextSibling.classList.add('unavailable')
                    if (!input.checked) return;
                    let inputsValue;
                    availableOptionInputsValue.length > 0 ? inputsValue = availableOptionInputsValue : inputsValue = optionInputsValue;
                    input.closest('.product-form__input').querySelector(`input[value="${inputsValue[0]}"]`).checked = true;
                    this.dispatchEvent(new Event('change'))
                }
            }
        });
    }
        
    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets.map((fieldset) => {
            return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        });
    }
}

customElements.define('variant-radios', VariantRadios);

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));

        this.querySelectorAll('button').forEach(
            (button) => button.addEventListener('click', this.onButtonClick.bind(this))
        );

        if (!this.checkHasMultipleVariants()) {
            this.initProductQuantity();
            this.checkVariantInventory();
        }
    }

    onInputChange(event) {
        event.preventDefault(); 
        var inputValue = this.input.value;
        var maxValue = parseInt(this.input.dataset.inventoryQuantity);
        var currentId = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="id"]')?.value;
        var saleOutStock  = document.getElementById('product-add-to-cart').dataset.available === 'true' || false ;
        const addButton = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="add"]');

        if(inputValue < 1) {
            inputValue = 1;

            this.input.value =  inputValue;
        }
        
        if (inputValue > maxValue && !saleOutStock && maxValue > 0) {
            var arrayInVarName = `selling_array_${this.input.dataset.product}`,
                itemInArray = window[arrayInVarName],
                itemStatus = itemInArray[currentId];
          
            if(itemStatus == 'deny') {
              inputValue = maxValue
              this.input.value = inputValue;
              const message = getInputMessage(maxValue)
              showWarning(message, 3000)
            }
        } else if (inputValue > maxValue && saleOutStock && maxValue <= 0) {
            this.input.value = inputValue;
        }

        if(window.subtotal.show) {
            var text,
                price = this.input.dataset.price,
                subTotal = 0;

            var parser = new DOMParser();

            subTotal = inputValue * price;
            subTotal = Shopify.formatMoney(subTotal, window.money_format);
            subTotal = extractContent(subTotal);

            const moneySpan = document.createElement('span')
            moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal') 
            moneySpan.innerText = subTotal 
            document.body.appendChild(moneySpan) 

            if (this.checkNeedToConvertCurrency()) {
                let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
            }

            subTotal = moneySpan.innerText 
            $(moneySpan).remove()

            if (window.subtotal.style == '1') {
                const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');

                pdView_subTotal.textContent = subTotal;
            }
            else if (window.subtotal.style == '2') {
                text = window.subtotal.text.replace('[value]', subTotal);

                addButton.textContent = text;  
            }

            const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
            const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

            if (stickyPrice.length) {
                stickyPrice.text(subTotal);
            }

            if (stickyComparePrice.length && window.subtotal.show) {
                let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price');
                comparePrice = inputValue * comparePrice;
                comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                comparePrice = extractContent(comparePrice);
                stickyComparePrice.text(comparePrice);
            }
        }

        if (this.classList.contains('quantity__group--2') || this.classList.contains('quantity__group--3')) {
            const mainQty = document.querySelector('.quantity__group--1 .quantity__input');
            mainQty.value = inputValue;

            const mainQty2Exists = !!document.querySelector('.quantity__group--2 .quantity__input');
            const mainQty3Exists = !!document.querySelector('.quantity__group--3 .quantity__input');

            if (this.classList.contains('quantity__group--2') && mainQty3Exists) {
                const mainQty3 = document.querySelector('.quantity__group--3 .quantity__input');
                mainQty3.value = inputValue;
            }
            else if (this.classList.contains('quantity__group--3') && mainQty2Exists) {
                const mainQty2 = document.querySelector('.quantity__group--2 .quantity__input');
                mainQty2.value = inputValue;
            }
        }
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        
        event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    checkNeedToConvertCurrency() {
        return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
    }
    
    checkHasMultipleVariants() {
        const arrayInVarName = `product_inven_array_${this.querySelector('[data-product]').dataset.product}`
        this.inven_array = window[arrayInVarName];
        return Object.keys(this.inven_array).length > 1
    }
        
    initProductQuantity() {
        if(this.inven_array != undefined) {
            var inven_num = Object.values(this.inven_array),
                inventoryQuantity = parseInt(inven_num);

            this.querySelector('input[name="quantity"]').setAttribute('data-inventory-quantity', inventoryQuantity);
            this.querySelector('input[name="quantity"]').dataset.inventoryQuantity = inventoryQuantity
        }
    }

    checkVariantInventory() {
        const addBtn = document.getElementById('product-add-to-cart')
        this.input.disabled = addBtn.disabled 
        this.querySelector('.btn-quantity.minus').disabled = addBtn.disabled
        this.querySelector('.btn-quantity.plus').disabled = addBtn.disabled
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(document.querySelector('.product-option [type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define('quantity-input', QuantityInput);

function hotStock(inventoryQuantity) {
    const productView = document.querySelector('.productView');
    const hotStock = productView.querySelector('.productView-hotStock');
    if(hotStock) {
        let hotStockText = hotStock.querySelector('.hotStock-text'),
            maxStock = parseFloat(hotStock.dataset.hotStock),
            textStock;

        if(inventoryQuantity > 0 && inventoryQuantity <= maxStock){
            hotStock.matches('.style-2') ? textStock  = window.inventory_text.hotStock2.replace('[inventory]', inventoryQuantity) : textStock  = window.inventory_text.hotStock.replace('[inventory]', inventoryQuantity);
            hotStockText.innerHTML = textStock;
            hotStock.classList.remove('is-hide');
        } else {
            hotStock.classList.add('is-hide');
        }

        if (hotStock.matches('.style-2')) {
            const invenProgress = inventoryQuantity / maxStock * 100,
                hotStockProgressItem = hotStock.querySelector('.hotStock-progress-item');
            hotStockProgressItem.style.width = `${invenProgress}%`;
        }
    }
}
const hotStockNoOptions = document.querySelector('.productView .productView-hotStock[data-current-inventory]');
if (hotStockNoOptions) {
    const inventoryQuantity = parseFloat(hotStockNoOptions.dataset.currentInventory);
    hotStock(inventoryQuantity);
}


function showWarning(content, time = null) {
    if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
    }
    const warningPopupContent = document.getElementById('halo-warning-popup').querySelector('[data-halo-warning-content]')
    warningPopupContent.textContent = content
    document.body.classList.add('has-warning')

    if (time) {
        window.warningTimeout = setTimeout(() => {
            document.body.classList.remove('has-warning')
        }, time)
    }
}

function getInputMessage(maxValue) {
    var message = window.cartStrings.addProductOutQuantity.replace('[maxQuantity]', maxValue);
    return message
}
